/**
 * ADR-004 allow-list. Shared so the mat page and the Worker use the same rule.
 * This module never fetches the board.
 */

export const MAX_BRACKET_URL = 2000;

/** Hosts from ADR-004. A subdomain of one of these is allowed. Nothing else is. */
export const BRACKET_HOSTS = ['trackwrestling.com', 'flowrestling.org', 'flowrestling.com'] as const;

export type BracketReason = 'invalid' | 'userinfo' | 'localhost' | 'ip' | 'scheme' | 'host';

export type BracketDecision = { ok: true; url: string | null } | { ok: false; reason: BracketReason; message: string };

const BRACKET_MESSAGES: Record<BracketReason, string> = {
  invalid: 'That board link is not a valid URL.',
  userinfo: 'The board link cannot include a username or password.',
  localhost: 'Localhost links are rejected. Paste the public https board link.',
  ip: 'An IP address is not an allowed board link.',
  scheme: 'The board link has to be https.',
  host: 'That host is not allowed. Use trackwrestling.com, flowrestling.org, flowrestling.com, or a subdomain of those.',
};

/**
 * Empty is allowed and means no link.
 * https only, on an allow-listed host, with no userinfo and no IP or localhost.
 */
export function decideBracketUrl(value: string): BracketDecision {
  const trimmed = value.trim();
  if (!trimmed) return { ok: true, url: null };
  if (trimmed.length > MAX_BRACKET_URL || /\s/.test(trimmed)) return reject('invalid');

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return reject('invalid');
  }

  if (parsed.username !== '' || parsed.password !== '') return reject('userinfo');

  const host = stripTrailingDot(parsed.hostname);
  if (host === 'localhost' || host.endsWith('.localhost')) return reject('localhost');
  if (isIpAddress(host)) return reject('ip');
  if (parsed.protocol !== 'https:') return reject('scheme');
  if (!isAllowedHost(host)) return reject('host');
  return { ok: true, url: parsed.href };
}

function reject(reason: BracketReason): BracketDecision {
  return { ok: false, reason, message: BRACKET_MESSAGES[reason] };
}

function stripTrailingDot(hostname: string): string {
  const host = hostname.toLowerCase();
  return host.endsWith('.') ? host.slice(0, -1) : host;
}

function isIpAddress(hostname: string): boolean {
  if (hostname.includes(':') || hostname.startsWith('[') || hostname.endsWith(']')) return true;
  return /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname);
}

function isAllowedHost(hostname: string): boolean {
  if (!hostname || hostname.startsWith('.') || hostname.endsWith('.') || hostname.includes('..')) return false;
  return BRACKET_HOSTS.some((allowed) => hostname === allowed || hostname.endsWith(`.${allowed}`));
}
